import React from 'react';
import { m } from 'framer-motion';
import { CheckIcon, BookOpenIcon, DumbbellIcon, ClipboardListIcon } from 'lucide-react';
import { SectionTag } from '../ui/SectionTag';
const tomosPillar = {
  icon: BookOpenIcon,
  tag: 'Incluido 📚',
  title: 'Los 6 Tomos del Sistema "Sesión en Mano"',
  value: '59,90€',
  today: 'Hoy: Incluido',
  lead: '180 sesiones profesionales para que nunca te falten ejercicios.',
  items: ['Fundamentos de Oro (6-9 años): El primer contacto con el baloncesto hecho bien.', 'Desarrollo Técnico (10-12 años): Manejo del balón, pases, lanzamientos y defensa con progresiones adaptadas a estas edades.', 'Transición Táctica (13-14 años): Contraataque, ocupación de espacios, defensa zonal y sistemas colectivos.', 'Construcción Competitiva (15-18 años): Sistemas de juego, bloqueos y situaciones de final de partido.', 'Especialidades: Preparación física, técnica avanzada de tiro y análisis del equipo rival.', 'Gestión de Equipos: Comunicación con jugadores y familias, liderazgo, psicología deportiva y organización de temporada.']
};
const bonosPillar = {
  icon: DumbbellIcon,
  tag: 'Incluido 💪',
  title: 'Los 5 Bonos Estratégicos',
  value: '59,50€',
  today: 'Hoy: Incluido',
  lead: 'Los recursos que hacen que el manual funcione desde el primer día.',
  items: ['Plan de Arranque en 7 Días: Por dónde empezar sin complicaciones.', '25 Calentamientos Inteligentes: Con balón y conectados al objetivo de la sesión.', 'Biblioteca de Juego Reducido: 30 situaciones (1v1, 2v2, 3v3) para enseñar a decidir.', 'Ruedas de Finalización Pro: 24 ejercicios de tiro por tipo de jugador y nivel.', 'Sistema Express: 20 sesiones para días de media pista, pocos jugadores o poco tiempo.']
};
const workbookBreakdown = ['6–9 años: 8 sesiones de 45 minutos.', '10–12 años: 8 sesiones de 60 minutos.', '13–14 años: 8 sesiones de 75 minutos.', '15–18 años: 8 sesiones de 90 minutos.', 'Especialidades y scouting: 6 sesiones de 75 minutos.', 'Gestión del equipo: 8 dinámicas guiadas y 10 plantillas imprimibles.'];
const workbookImages = ['https://cdn.magicpatterns.com/uploads/izkeBDbt7bGMTmViCfHqhY/Tecnica-tiro.webp', 'https://cdn.magicpatterns.com/uploads/sjd2NveVmTp9vFzJjvc6XQ/Sesion-15-18-09.webp', 'https://cdn.magicpatterns.com/uploads/izoiRLkdsihU9pHhf1Nsga/Lamina-defensa_(1).webp', 'https://cdn.magicpatterns.com/uploads/4mczM3EXvVvwVGtxdURSDC/Lamina-ataque.webp'];
function ContentPillar({
  pillar,
  delay
}: {
  pillar: typeof tomosPillar;
  delay: number;
}) {
  return <m.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.45,
    delay
  }} className="flex flex-col rounded-2xl border border-white/10 bg-bg-secondary p-6 sm:p-7" data-id="element-283" data-mp="2u96o66kdhwqk">
      <div className="flex items-start justify-between gap-3" data-id="element-284" data-mp="zvp6v1ak9w3z">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-orange/10" data-id="element-285" data-mp="vuvk4b8uunco">
          <pillar.icon size={22} className="text-orange" data-id="element-286" data-mp="lr6ro5etjxo2" />
        </div>
        <span className="rounded-full bg-white/5 px-3 py-1 text-xs font-semibold text-muted" data-id="element-287" data-mp="ps0eevgiz6fd">
          {pillar.tag}
        </span>
      </div>
      <h3 className="mt-4 font-heading text-lg font-semibold leading-snug text-white sm:text-xl" data-id="element-288" data-mp="bztz1bl753gz">
        {pillar.title}
      </h3>
      <div className="mt-2 flex items-baseline gap-2 text-sm" data-id="element-289" data-mp="21wbkqb5vapf">
        <span className="text-muted line-through" data-id="element-290" data-mp="20cy17gw3drc">Valor: {pillar.value}</span>
        <span className="font-semibold text-success" data-id="element-291" data-mp="7225kd4wl9ao">{pillar.today}</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-292" data-mp="3p7dvcbx8huwu">{pillar.lead}</p>
      <ul className="mt-4 space-y-2.5" data-id="element-293" data-mp="3u5fcgrb3nk5o">
        {pillar.items.map(item => <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-white/90" data-id="element-294" data-mp="3u7jppzuw1vim">
            <CheckIcon size={16} className="mt-0.5 flex-shrink-0 text-success" data-id="element-295" data-mp="1ndemrybhxrnu" />
            <span data-id="element-296" data-mp="1sdkefehrne5x">{item}</span>
          </li>)}
      </ul>
    </m.div>;
}
export function WhatYouGet() {
  return <section className="bg-bg-primary py-10 sm:py-14" data-id="element-297" data-mp="3jidv8h6rbfwo">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6" data-id="element-298" data-mp="3jix5dbq61005">
        <SectionTag icon="🎁" data-id="element-299" data-mp="3eij7gmia36fu">Lo que recibes hoy (Acceso Inmediato)</SectionTag>
        <h2 className="mt-5 font-heading text-3xl font-bold uppercase leading-tight text-white sm:text-4xl" data-id="element-300" data-mp="3ahz95flyxws4">
          Todo lo que necesitas, en un solo lugar.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg" data-id="element-301" data-mp="35kpcpg05wkus">
          180 sesiones para que preparar tus entrenamientos deje de quitarte horas.
        </p>
        <p className="mt-2 text-sm font-medium text-orange" data-id="element-302" data-mp="2vtcjol2mnon6">
          Más de 180 sesiones · 6 Tomos · +10 Meses de entrenamientos
        </p>
      </div>

      <div className="mx-auto mt-8 flex max-w-3xl flex-col gap-6 px-4 sm:px-6" data-id="element-303" data-mp="2mrv6pbkio32p">
        <ContentPillar pillar={tomosPillar} delay={0} data-id="element-304" data-mp="2mtxya01bi49j" />

        <m.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        delay: 0.08
      }} className="flex flex-col rounded-2xl border border-success/30 bg-bg-secondary p-6 sm:p-7" data-id="element-305" data-mp="2hucu9b7aj5py">
          <div className="flex items-start justify-between gap-3" data-id="element-306" data-mp="16c79j390h3qa">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-success/10" data-id="element-307" data-mp="1bb0o6z01b0s1">
              <ClipboardListIcon size={22} className="text-success" data-id="element-308" data-mp="elj7zq3fdwc3" />
            </div>
            <span className="rounded-full bg-success/10 px-3 py-1 text-xs font-semibold text-success" data-id="element-309" data-mp="ilbmejfi2wpd">
              Nuevo: Incluido
            </span>
          </div>
          <h3 className="mt-4 font-heading text-lg font-semibold leading-snug text-white sm:text-xl" data-id="element-310" data-mp="sbwgbtk8bd1y">
            6 Cuadernos Prácticos Visuales
          </h3>
          <p className="mt-1 text-sm font-medium text-success" data-id="element-311" data-mp="3r1df0ny4r9gk">Del manual a la cancha, con entrenamientos preparados.</p>

          <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-312" data-mp="3vywqxh7cva5c">
            Además de las 180 sesiones de los tomos, recibes seis cuadernos complementarios con 38 sesiones
            adicionales repartidas por edades y objetivos, más 8 dinámicas de comunicación y organización
            del equipo.
          </p>

          <ul className="mt-4 space-y-2" data-id="element-313" data-mp="17x7tz9pbj7rt">
            {workbookBreakdown.map(item => <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-white/90" data-id="element-314" data-mp="17zlrdp7h0w2j">
                <CheckIcon size={16} className="mt-0.5 flex-shrink-0 text-success" data-id="element-315" data-mp="f8k6d7h3k6b3" />
                <span data-id="element-316" data-mp="fawvq5p6piy6">{item}</span>
              </li>)}
          </ul>

          <p className="mt-4 text-sm leading-relaxed text-muted" data-id="element-317" data-mp="t9murqvkdsp6">
            Los cuadernos de entrenamiento incluyen tiempos por bloque, organización de jugadores,
            ejercicios con diagramas, rotaciones y ajustes de dificultad.
          </p>
          <p className="mt-2 text-sm font-medium text-white" data-id="element-318" data-mp="1zox9p077vhry">
            240 páginas adicionales para consultar, imprimir y utilizar al preparar tus entrenamientos.
          </p>

          <p className="mx-auto mt-5 w-fit rounded-full bg-success/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-success" data-id="element-319" data-mp="2c6rhqofbts3c">
            Incluidos con tu compra
          </p>

          <div className="mt-4 scrollbar-hide flex gap-3 overflow-x-auto pb-1" data-id="element-320" data-mp="236bgzmpywz26">
            {workbookImages.map((src, i) => <img key={src} src={src} alt={`Página real de uno de los 6 cuadernos prácticos (ejemplo ${i + 1})`} loading="lazy" decoding="async" className="aspect-[3/4] w-[180px] flex-shrink-0 rounded-lg border border-white/10 object-cover sm:w-[210px]" data-id="element-321" data-mp="2qvnlwae9ikyo" data-mpi={i} />)}
          </div>
          <p className="mt-2 text-center text-xs text-muted" data-id="element-322" data-mp="39ui2loxadvhh">Páginas reales de los cuadernos incluidos.</p>
        </m.div>

        <ContentPillar pillar={bonosPillar} delay={0.16} data-id="element-323" data-mp="3em9ty1o9o58o" />
      </div>
    </section>;
}